import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  MutableRefObject,
  useCallback,
} from "react";

import * as fromProjects from "store/Projects";

import { a } from "@react-spring/web";
import { useIntersect } from "hooks/useIntersect";
import { useAnimateInTrail } from "hooks/useAnimateInTrail";
import {
  initialOffset,
  animationTriggerThreshold,
} from "components/Animations/SpringProperties/SpringProperties";

import * as fromProjectHeroSections from "components/Project/ProjectHeroSections/Index";

interface IProps {
  wrapperRef: MutableRefObject<HTMLDivElement>;
  animationReady: boolean;
  details: fromProjects.IProjectHeroSection;
  backgroundImage: string;
}

const WorkDetailsHeroImageComponent: React.FC<IProps> = ({
  wrapperRef,
  animationReady,
  details,
  backgroundImage,
  ...props
}) => {
  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
    rootMargin: "-150px 0px -150px 0px",
  });

  const [animateInTrail, triggerAnimateIn] = useAnimateInTrail(inView, 2, {
    notify: [
      {
        index: 1,
        threshold: animationTriggerThreshold,
      },
    ],
  });

  const wrapperStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const getHeroComponent = useCallback(() => {
    const componentName = details.type;
    const imageComponentCommonProps = {
      wrapperRef,
      heroSection: details,
      animateIn: triggerAnimateIn(1),
    };

    switch (componentName) {
      case "threeFloatingScreenshots":
        return (
          <fromProjectHeroSections.ThreeFloatingScreenshots
            {...imageComponentCommonProps}
          />
        );
      case "fannedDocument":
        return (
          <fromProjectHeroSections.FannedDocument
            {...imageComponentCommonProps}
          />
        );
      case "depthParallaxImage":
        return (
          <fromProjectHeroSections.DepthParallaxImage
            {...imageComponentCommonProps}
          />
        );
      default:
        // The following line guarantees that every action in the KnownAction union has been covered by a case above
        const exhaustiveCheck: never = componentName;
    }
  }, [animationReady, triggerAnimateIn]);

  const backgroundImageStyles = {
    backgroundImage: "url(/assets/images/work/" + backgroundImage + ")",
  };

  return (
    <div className="featuredImageWrapper">
      <a.div
        ref={inViewRef}
        className="featuredImageContainer"
        style={wrapperStyles}
      >
        <div className="background" style={backgroundImageStyles} />
        <div className="contentWrapper">{getHeroComponent()}</div>
      </a.div>
    </div>
  );
};

export const WorkDetailsHeroImage = WorkDetailsHeroImageComponent;
